import { z } from 'zod';

/**
 * Core Jarvis widget DATA schemas (IDEA-83 increment 2 / ADR 0100 pillar 3 — the generic,
 * service-agnostic display kinds `table` / `key-value` / `code` / `sparkline`).
 *
 * WHY here (not in `@squadlab/jarvis`): the `widget` entry (`jarvis-v1.ts`) carries `data: unknown`;
 * the WIDGET'S OWN schema parses it at render time in `@squadlab/jarvis` (parse, don't validate —
 * ADR 0005). But `@squadlab/jarvis` is deliberately zod-free (it consumes schemas through a
 * structural `safeParse` surface — "no zod plumbing" — the same reason the capability arg schemas
 * live here), and the repo forbids adding a dependency without sign-off. A versioned display shape
 * is also a genuine CONTRACT between the SERVER-SIDE author (assistant/tools) and the browser
 * renderer, so it belongs in `@squadlab/contracts`.
 *
 * SECURE + BOUNDED by design (untrusted, model/tool-authored content — the DoS/XSS surface, ADR
 * 0100): SHORT display strings are length-capped HERE (an over-long string ⇒ a visible fallback at
 * render time). Array element COUNTS carry a GENEROUS `.max()` cap HERE too (IDEA-83 increment 3
 * hardening): widgets became MODEL-AUTHORABLE with the interactive fast-follow, so a hostile HUGE
 * array must be REJECTED at parse (→ the visible fallback) rather than fully materialised and only
 * then sliced — the increment-1 DoS class, now reachable. The caps sit ABOVE the renderer's DISPLAY
 * caps (`MAX_TABLE_ROWS/COLS`, `MAX_WIDGET_KV_ITEMS`, `MAX_WIDGET_SPARKLINE_POINTS` in
 * `@squadlab/jarvis`), so a REASONABLE oversize still parses and degrades to a bounded slice-with-
 * notice; only a truly pathological array is refused outright.
 */

/** Length cap for a short display string (a table cell / kv label+value / sparkline label). */
export const WIDGET_TEXT_MAX_CHARS = 512;
/** Length cap for a code widget's language hint (it rides as a charset-safe `data-lang` in the DOM). */
export const WIDGET_LANG_MAX_CHARS = 64;
/** Length cap for an idea id echoed into a widget (mirrors the `ui.read.idea` id arg bound). */
export const WIDGET_IDEA_ID_MAX_CHARS = 120;
/** Length cap for a verdict label (a short band token like `strong-go`; bounded for DoS + display). */
export const WIDGET_VERDICT_MAX_CHARS = 64;

// ── Array element-count caps (IDEA-83 increment 3 — DoS hardening for model-authorable widgets).
//    Each sits ABOVE the renderer's DISPLAY cap so a reasonable oversize still parses + slices;
//    only a pathological array is parse-rejected into the visible fallback. ──
/** Max table COLUMNS a widget may carry (renderer displays the first `MAX_TABLE_COLS`). */
export const WIDGET_TABLE_MAX_COLUMNS = 128;
/** Max table ROWS a widget may carry (renderer displays the first `MAX_TABLE_ROWS`). */
export const WIDGET_TABLE_MAX_ROWS = 2_000;
/** Max key-value ITEMS a widget may carry (renderer displays the first `MAX_WIDGET_KV_ITEMS`). */
export const WIDGET_KV_MAX_ITEMS = 1_000;
/** Max sparkline POINTS a widget may carry (renderer plots the first `MAX_WIDGET_SPARKLINE_POINTS`). */
export const WIDGET_SPARKLINE_MAX_POINTS = 4_096;

/**
 * `table` — a real tabular view. `columns` = header labels; `rows` = string cells (each row is
 * itself capped at {@link WIDGET_TABLE_MAX_COLUMNS} so a single hostile row can't balloon either).
 */
export const WidgetTableDataSchema = z.object({
  columns: z.array(z.string().max(WIDGET_TEXT_MAX_CHARS)).max(WIDGET_TABLE_MAX_COLUMNS),
  rows: z.array(z.array(z.string().max(WIDGET_TEXT_MAX_CHARS)).max(WIDGET_TABLE_MAX_COLUMNS)).max(WIDGET_TABLE_MAX_ROWS),
});
export type WidgetTableData = z.infer<typeof WidgetTableDataSchema>;

/** `key-value` — labelled facts rendered as a definition list. */
export const WidgetKeyValueDataSchema = z.object({
  items: z
    .array(
      z.object({
        label: z.string().max(WIDGET_TEXT_MAX_CHARS),
        value: z.string().max(WIDGET_TEXT_MAX_CHARS),
      }),
    )
    .max(WIDGET_KV_MAX_ITEMS),
});
export type WidgetKeyValueData = z.infer<typeof WidgetKeyValueDataSchema>;

/**
 * `code` — a verbatim code block. `text` is DELIBERATELY not `.max()`-capped here (unlike the
 * ARRAY fields above, which reject at parse): validating a huge array is O(n) work worth refusing,
 * but a single string is O(1) to hold and is already bounded where it matters — the renderer slices
 * it to `MAX_WIDGET_CODE_CHARS` into one text node (truncate-with-notice), so an oversize code block
 * degrades to a bounded view rather than a fallback. (The string is materialised by the entry's
 * JSON.parse before any schema runs, so a cap here would not prevent materialisation anyway.)
 */
export const WidgetCodeDataSchema = z.object({
  language: z.string().max(WIDGET_LANG_MAX_CHARS).optional(),
  text: z.string(),
});
export type WidgetCodeData = z.infer<typeof WidgetCodeDataSchema>;

/**
 * Accept ANY JS number — including `NaN`/±`Infinity` (`typeof === 'number'`) — so the sparkline
 * renderer can GUARD non-finite/empty points into a visible PLACEHOLDER (ADR 0100 determinism §)
 * rather than a total parse-fallback. A non-number element (string/object) still fails the parse.
 * JSON can't carry NaN/Infinity, so on the wire this only ever sees finite numbers; the guard is
 * defence-in-depth for programmatically-authored data.
 */
const widgetNumber = z.custom<number>((v) => typeof v === 'number', { message: 'expected a number' });

/** `sparkline` — a compact trend line drawn from a numeric series onto a deterministic `<canvas>`. */
export const WidgetSparklineDataSchema = z.object({
  points: z.array(widgetNumber).max(WIDGET_SPARKLINE_MAX_POINTS),
  label: z.string().max(WIDGET_TEXT_MAX_CHARS).optional(),
});
export type WidgetSparklineData = z.infer<typeof WidgetSparklineDataSchema>;

/**
 * `idea-summary` — one idea's headline card (title + a verdict badge + the key market metrics).
 * First shipped as an idea-engine widget (IDEA-83 increment 5), then promoted to a DOMAIN-FREE CORE
 * kind in `@squadlab/jarvis` (IDEA-83 Part C / ADR 0100 pillar 3): its renderer is registered by
 * `registerCoreWidgets`, so EVERY service's dock renders the read-only card — a Jarvis session viewed
 * from another service shows the card, not the unknown-kind fallback. The idea-engine-specific "Open"
 * affordance is NOT part of the renderer: it rides the widget ENTRY as an action descriptor (attached
 * by `ui.show.idea`) and appears only where `idea.open` is operable. The shape is a CONTRACT between
 * the server-side author (assistant/tools) and that browser renderer, so it lives here beside its siblings.
 *
 * `verdict` is a free-ish short string (the projection sends the verdict band, e.g. `strong-go`);
 * the renderer maps it to a badge class through a FIXED allowlist (never a class from raw data) and
 * shows the text via `textContent`. The numeric metrics are OPTIONAL: a not-yet-evaluated idea has
 * no TAM/ROI/margin, and the renderer shows an em-dash rather than a `NaN`.
 */
export const WidgetIdeaSummaryDataSchema = z.object({
  ideaId: z.string().max(WIDGET_IDEA_ID_MAX_CHARS),
  title: z.string().max(WIDGET_TEXT_MAX_CHARS),
  verdict: z.string().max(WIDGET_VERDICT_MAX_CHARS),
  tamUsd: z.number().optional(),
  roiMultiple: z.number().optional(),
  marginFraction: z.number().optional(),
});
export type WidgetIdeaSummaryData = z.infer<typeof WidgetIdeaSummaryDataSchema>;
